import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useQuery, useReadQuery } from '@apollo/client/react'
import {
  ConfirmDialog,
  ErrorAlert,
  PageHeader,
  PaginationControl,
  RouteLoadingIndicator,
} from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import Footer from '@web/src/features/footer/footer'
import RendezvousCard from '@web/src/features/rendezvous/rendezvous-card/rendezvous-card'
import {
  DEFAULT_RENDEZVOUS_LIST_DISTANCE,
  type RendezvousListFilterFormValues,
  rendezvousListFilterFormValuesFromResolvedParams,
} from '@web/src/features/rendezvous/rendezvous-list-filter-types'
import RendezvousListSideNav from '@web/src/features/rendezvous/rendezvous-list-side-nav/rendezvous-list-side-nav'
import RendezvousListTopNav from '@web/src/features/rendezvous/rendezvous-list-top-nav/rendezvous-list-top-nav'
import RendezvousMenu from '@web/src/features/rendezvous/rendezvous-menu/rendezvous-menu'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { CoffeeIcon, PlusIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type { RendezvousQuery, RendezvousQueryVariables, RendezvousSortEnum, RendezvousTypeEnum } from 'types/graphql'
import { z } from 'zod'

const RENDEZVOUS_ENTRY_FIELDS = gql`
  fragment RendezvousListEntryFields on Rendezvous {
    id
    canEdit
    canDelete
    canCancel
    canReport
    rendezvousTime
    rendezvousType
    description
    location
    distance
    profile {
      id
      name
      minAge
      maxAge
      lastOnline
      supporter
      profileMedia {
        id
        ownerId
        reviewStatus
        imageUrlSmall
        imageUrlMedium
        nsfwRating
      }
    }
  }
`

type RendezvousListEntry = NonNullable<RendezvousQuery['rendezvousPage']>['entries'][number]

const RENDEZVOUS_BY_ID_QUERY: TypedDocumentNode<{ rendezvous: RendezvousListEntry | null }, { id: string }> = gql`
  query RendezvousByIdQuery($id: String!) {
    rendezvous(id: $id) {
      ...RendezvousListEntryFields
    }
  }
  ${RENDEZVOUS_ENTRY_FIELDS}
`

export const QUERY: TypedDocumentNode<RendezvousQuery, RendezvousQueryVariables> = gql`
  query RendezvousQuery($page: Int!, $filters: RendezvousFilters, $sortBy: RendezvousSortEnum) {
    rendezvousPage(page: $page, filters: $filters, sortBy: $sortBy) {
      entries {
        ...RendezvousListEntryFields
      }
      count
      page
      perPage
    }
  }
  ${RENDEZVOUS_ENTRY_FIELDS}
`

const DELETE_RENDEZVOUS_MUTATION = gql`
  mutation DeleteRendezvousMutation($id: String!) {
    deleteRendezvous(id: $id) {
      id
    }
  }
`

type RendezvousListLoaderDeps = {
  page: number
  name: string
  distance: number
  rendezvousType?: string[]
  sortBy: string
  showPast: boolean
  tab: string
}

type RendezvousListSearchInput = {
  page?: number
  search?: string
  name?: string
  distance?: number
  rendezvousType?: string[]
  sortBy?: string
  showPast?: boolean
  tab?: string
  id?: string
}

function resolveRendezvousListSearchParams(search: RendezvousListSearchInput): RendezvousListLoaderDeps {
  return {
    page: search.page ?? 1,
    name: search.search || search.name || '',
    distance: search.distance ?? DEFAULT_RENDEZVOUS_LIST_DISTANCE,
    rendezvousType: search.rendezvousType,
    sortBy: search.sortBy ?? 'TIME',
    showPast: search.showPast ?? false,
    tab: search.tab ?? 'all',
  }
}

function rendezvousListQueryVariablesFromDeps(deps: RendezvousListLoaderDeps): RendezvousQueryVariables {
  return {
    page: deps.page,
    filters: {
      rendezvousType: (deps.rendezvousType as RendezvousTypeEnum[]) || undefined,
      distance: deps.distance,
      showPast: deps.showPast,
      myRendezvous: deps.tab === 'mine',
      search: deps.name || undefined,
    },
    sortBy: deps.sortBy as RendezvousSortEnum,
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/rendezvous/')({
  component: RendezvousListPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    search: z.string().optional(),
    name: z.string().optional(),
    distance: z.coerce.number().optional(),
    rendezvousType: z.array(z.string()).optional(),
    sortBy: z.string().optional(),
    showPast: z.coerce.boolean().optional(),
    tab: z.string().optional(),
    id: z.string().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
        name: '',
        search: '',
        distance: DEFAULT_RENDEZVOUS_LIST_DISTANCE,
        rendezvousType: [],
        sortBy: 'TIME',
        showPast: false,
        tab: 'all',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Rendezvous List Page | ${env.VITE_APP_NAME}`,
      description:
        'Discover exciting casual rendezvous opportunities near you. Connect with like-minded people for coffee dates, dinner meetups, and fun activities in your local area.',
      robots: robotsMetaForEnvironment({
        production: 'index, follow',
        staging: 'noindex,nofollow',
      }),
    }),
  loaderDeps: ({ search }) => resolveRendezvousListSearchParams(search),
  loader: async ({ context, deps: d }) => {
    const deps = d as RendezvousListLoaderDeps
    const { queryRef } = await queryAndPreload(context, QUERY, {
      variables: rendezvousListQueryVariablesFromDeps(deps),
    })
    return { queryRef }
  },
})

function CreateRendezvousButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission, isAuthenticated } = useAuth()

  if (!(isAuthenticated && hasPermission('rendezvous:create'))) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={t('Create Rendezvous')}
            onClick={() => navigate({ to: '/rendezvous/create' })}
            size="icon"
            type="button"
          >
            <PlusIcon className="size-4 stroke-3" />
          </Button>
        }
      />
      <TooltipContent>{t('Create Rendezvous')}</TooltipContent>
    </Tooltip>
  )
}

function RendezvousListPage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { id } = Route.useSearch()
  const search = useSearch({ strict: false })
  const { queryRef } = Route.useLoaderData()
  const { data, error } = useReadQuery(queryRef as QueryRef<RendezvousQuery>)
  const highlightedRendezvous = useQuery(RENDEZVOUS_BY_ID_QUERY, {
    variables: { id: id ?? '' },
    skip: !id,
  })
  const searchRef = useRef(search)
  searchRef.current = search
  const navigate = useNavigate()
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)

  const [currentId, setCurrentId] = useState<string | null>(id || null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [rendezvousToDelete, setRendezvousToDelete] = useState<string | null>(null)
  const [reportModal, setReportModal] = useState(false)
  const [rendezvousToReport, setRendezvousToReport] = useState<string | null>(null)

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  const isFilterLoading = filterNavigatePending && isRoutePending
  const resolvedSearch = useMemo(() => resolveRendezvousListSearchParams(search), [search])
  const showMyRendezvous = resolvedSearch.tab === 'mine'

  const listFilterDefaults = useMemo(
    (): RendezvousListFilterFormValues =>
      rendezvousListFilterFormValuesFromResolvedParams({
        name: resolvedSearch.name,
        distance: resolvedSearch.distance,
        rendezvousType: resolvedSearch.rendezvousType,
        sortBy: resolvedSearch.sortBy,
        showPast: resolvedSearch.showPast,
      }),
    [
      resolvedSearch.name,
      resolvedSearch.distance,
      resolvedSearch.rendezvousType,
      resolvedSearch.sortBy,
      resolvedSearch.showPast,
    ]
  )

  const listFilterResetValues = useMemo(
    (): RendezvousListFilterFormValues =>
      rendezvousListFilterFormValuesFromResolvedParams({
        name: undefined,
        distance: DEFAULT_RENDEZVOUS_LIST_DISTANCE,
        rendezvousType: undefined,
        sortBy: undefined,
        showPast: false,
      }),
    []
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: resolvedSearch.sortBy },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: resolvedSearch.sortBy })
  }, [resolvedSearch.sortBy, sortFormMethods])

  const [deleteRendezvous] = useMutation(DELETE_RENDEZVOUS_MUTATION, {
    onCompleted: () => {
      toast.success(t('Rendezvous deleted successfully!'))
      navigate({
        search: {
          ...searchRef.current,
        },
      })
    },
    onError: (e) => {
      toast.error(e.message)
    },
  })

  const applyListFilters = useCallback(
    (values: RendezvousListFilterFormValues, options: { includeSort: boolean }) => {
      setCurrentId(null)
      setFilterNavigatePending(true)
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          id: undefined,
          search: values.name || undefined,
          name: undefined,
          distance: values.distance[0],
          rendezvousType: values.rendezvousType.length > 0 ? values.rendezvousType : undefined,
          showPast: values.showPast || undefined,
          tab: resolvedSearch.tab,
          ...(options.includeSort ? { sortBy: values.sortBy || undefined } : {}),
        },
        replace: true,
      })
    },
    [navigate, resolvedSearch.tab]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handleReset = useCallback(() => {
    setCurrentId(null)
    navigate({
      to: '/rendezvous',
      search: {
        ...(showMyRendezvous ? { tab: 'mine' } : {}),
      },
      replace: true,
    } as never)
  }, [navigate, showMyRendezvous])

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      navigate({
        search: {
          ...searchRef.current,
          page: nextPage,
        },
        replace: true,
      })
      window.scrollTo(0, 0)
    },
    [navigate]
  )

  const handleEdit = (idValue: string) => {
    navigate({ to: '/rendezvous/$id/edit', params: { id: idValue } } as never)
  }

  const handleDelete = (idValue: string) => {
    setRendezvousToDelete(idValue)
    setDeleteModal(true)
  }

  const handleReport = (idValue: string) => {
    setRendezvousToReport(idValue)
    setReportModal(true)
  }

  const handleConfirmDelete = () => {
    if (rendezvousToDelete) {
      deleteRendezvous({ variables: { id: rendezvousToDelete } })
      setDeleteModal(false)
      setRendezvousToDelete(null)
    }
  }

  const rendezvousData = useMemo(() => {
    const pageData = data?.rendezvousPage || { entries: [], count: 0, perPage: 24, page: 1 }
    const highlighted = highlightedRendezvous.data?.rendezvous
    if (!highlighted || pageData.entries.some((entry) => entry.id === highlighted.id)) {
      return pageData
    }
    return {
      ...pageData,
      entries: [highlighted, ...pageData.entries],
    }
  }, [data?.rendezvousPage, highlightedRendezvous.data?.rendezvous])

  const getPagePreTitle = useCallback(() => {
    if (showMyRendezvous) return t('My')
    return t('Discover')
  }, [showMyRendezvous, t])

  const getBookmarkTitle = useCallback(() => {
    if (showMyRendezvous) return t('My Rendezvous')
    return t('Discover Rendezvous')
  }, [showMyRendezvous, t])

  const getPageSubtitle = useCallback(() => {
    if (showMyRendezvous) return t('Browse rendezvous you have created.')
    return t(
      'Discover exciting casual rendezvous opportunities near you. Connect with like-minded people for coffee dates, dinner meetups, and fun activities in your local area.'
    )
  }, [showMyRendezvous, t])

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <>
      <main className="flex min-h-0 w-full flex-col md:flex-row">
        <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
          <RendezvousListSideNav
            filterDefaults={listFilterDefaults}
            filterResetValues={listFilterResetValues}
            isAuthenticated={isAuthenticated}
            onFiltersApply={(values) => applyListFilters(values, { includeSort: false })}
            onReset={handleReset}
          />
        </aside>

        <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
          <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
          <div className="mx-auto w-full max-w-none px-4 py-2 md:px-8 lg:px-16">
            <PageHeader
              breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: getBookmarkTitle() }]}
              className="items-stretch pb-0 pb-4 max-md:pb-0 md:mb-4"
              highlightTitle={t('Rendezvous')}
              preTitle={getPagePreTitle()}
              rightChild={
                <>
                  <div className="flex items-start justify-end md:hidden">
                    <CreateRendezvousButton />
                  </div>
                  <div className="hidden h-full flex-col items-end gap-3 md:flex">
                    <CreateRendezvousButton />

                    <div className="mt-auto w-full">
                      <div className="flex flex-row items-center gap-2">
                        <span className="shrink-0 justify-self-center whitespace-nowrap font-medium text-muted-foreground text-sm">
                          {t('Sort By:')}
                        </span>
                        <Form formMethods={sortFormMethods} suppressHydrationWarning>
                          <NativeSelectFormField
                            fieldProps={{ className: 'min-w-0 flex-1' }}
                            name="sortBy"
                            onValueChange={handleSortChange}
                            selectProps={{ className: 'w-full min-w-[10rem]' }}
                          >
                            <option value="DISTANCE">{t('Distance')}</option>
                            <option value="TIME">{t('Time')}</option>
                            <option value="TYPE">{t('Type')}</option>
                            <option value="NAME">{t('Profile Name')}</option>
                          </NativeSelectFormField>
                        </Form>
                      </div>
                    </div>
                  </div>
                </>
              }
              subtitle={getPageSubtitle()}
            />

            <div className="mb-6 w-full md:hidden">
              <div className="mt-4">
                <RendezvousListTopNav
                  filterDefaults={listFilterDefaults}
                  filterResetValues={listFilterResetValues}
                  isAuthenticated={isAuthenticated}
                  onFiltersApply={(values) => applyListFilters(values, { includeSort: true })}
                  onReset={handleReset}
                  rightBarChildren={<CreateRendezvousButton />}
                />
              </div>
            </div>
            <div className="mb-8">
              <RendezvousMenu activeTab={resolvedSearch.tab} />
            </div>

            {rendezvousData.entries.length > 0 ? (
              <div className="flex w-full flex-col items-stretch gap-0">
                <div className="grid w-full grid-cols-1 items-stretch gap-4 md:gap-6">
                  {rendezvousData.entries.map((rendezvous) => (
                    <RendezvousCard
                      currentId={currentId ?? undefined}
                      key={rendezvous.id}
                      onDelete={handleDelete}
                      onEdit={handleEdit}
                      onReport={handleReport}
                      rendezvous={{
                        ...rendezvous,
                        canEdit: rendezvous.canEdit ?? false,
                        canDelete: rendezvous.canDelete ?? false,
                        canCancel: rendezvous.canCancel ?? false,
                        canReport: rendezvous.canReport ?? false,
                        rendezvousTime: rendezvous.rendezvousTime?.toString() ?? '',
                      }}
                    />
                  ))}
                </div>

                <PaginationControl
                  count={rendezvousData.count}
                  onPageChange={(e) => handlePageChange(e.page)}
                  page={resolvedSearch.page}
                  pageSize={rendezvousData.perPage}
                />
              </div>
            ) : (
              <Empty className="w-full pb-20">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CoffeeIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t('No rendezvous found')}</EmptyTitle>
                  <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </div>

          <Footer />
        </div>
      </main>

      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Yes')}
        onConfirm={handleConfirmDelete}
        onOpenChange={(details) => setDeleteModal(details.open)}
        open={deleteModal}
        text={t('Are you sure you want to delete this rendezvous?')}
        title={t('Are you sure?')}
        variant="delete"
      />
      <ReportCreate
        onOpenChange={(details) => {
          setReportModal(details.open)
          if (!details.open) {
            setRendezvousToReport(null)
          }
        }}
        open={reportModal}
        targetId={rendezvousToReport ?? undefined}
        targetKind="RENDEZVOUS"
      />
    </>
  )
}
